import { CommonModule } from '@angular/common';
import { Component, effect, EventEmitter, HostListener, inject, Input, OnDestroy, OnInit, Output, Signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CreateEventDialogComponent } from 'src/app/components/shared/dialogs/create-event-dialog/create-event-dialog.component';
import { Project } from 'src/app/interfaces/api/project';
import { Unit } from 'src/app/interfaces/api/unit';
import { User } from 'src/app/interfaces/api/user';
import { LabelPositions, PlayerEventValue, PlayerTick } from 'src/app/interfaces/shared/planning-player';

import { Tracker } from 'src/app/interfaces/api/tracker';
import { ProjectService } from 'src/app/services/api/projects/project.service';
import { AuthService } from 'src/app/services/core/auth.service';
import { PlanningStore } from 'src/app/services/stores/planning.store';
import { ButtonComponent, DatePickerComponent } from 'ui-library';

@Component({
  selector: 'app-planning-player',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    TranslateModule,
    DatePickerComponent,
    ButtonComponent
  ],
  templateUrl: './planning-player.component.html',
  styleUrls: ['./planning-player.component.scss']
})

export class PlanningPlayerComponent implements OnInit, OnDestroy {
  // Services
  private readonly auth: AuthService = inject(AuthService);
  private readonly dialog: MatDialog = inject(MatDialog);
  private readonly translate: TranslateService = inject(TranslateService);
  private readonly projectService: ProjectService = inject(ProjectService);
  private readonly planningStore: PlanningStore = inject(PlanningStore);

  // Component API
  @Output() onPlayerChanged = new EventEmitter<PlayerEventValue>();
  @Input('playerValue') playerValueObs!: BehaviorSubject<number>;

  // Variables
  playerValue: number = 0;
  name: string = "viewer";
  isInit: boolean = false;
  playerTimeline: any;
  playMode: string = 'replay';
  playerInterval: any = null;
  min: number = 0;
  thumbLabel: boolean = false;
  playerRawValue: number = 0;
  playerTimeSeek: number = 1;
  playAllMode: boolean = false;
  recordState: string = 'stop';
  recordValue: number = 0;
  recordInterval: any = null;
  eventTitle: string = "Untitled";
  eventComment: string = "";
  units: Unit[] = [];
  trackers: Tracker[] = [];
  playerState: string = 'stop';
  playerDuration: number = 0;
  ticks: PlayerTick[] = [];
  playerDate: moment.Moment = moment().subtract(6, 'hours');
  startDate: moment.Moment = moment().subtract(6, 'hours');
  endDate: moment.Moment = moment();
  max: number = moment(this.endDate).diff(this.startDate, 'seconds');
  currentDate: string = moment(this.playerDate).format('DD/MM/YYYY HH:mm:ss');
  speed: number = 1;
  defaultLang!: string;
  public viewer: any;
  holdTimeout: NodeJS.Timeout | null = null;
  destroy$ = new Subject<void>();

  // Signals
  readonly project: Signal<Project> = this.projectService.currentProjectSignal;
  readonly user: Signal<User> = this.auth.currentUser;

  @HostListener('window:keydown', ['$event'])
  handleKeydown(event: KeyboardEvent) {
    if ((event.target as HTMLElement)?.tagName !== 'BODY') return;
    if (event.key === ' ') {
      this.onPlay()
    }
    if (event.key === '6') {
      this.onForwardHold();
    }
    if (event.key === '4') {
      this.onRewardHold();
    }
    if (event.key === '2') {
      this.onForward();
    }
    if (event.key === '8') {
      this.onReward();
    }
  }

  constructor() {
    // this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe(params => {
    //   if (params['startDate']) {
    //     this.startDate = moment(params['startDate']);
    //     this.currentDate = moment(params['startDate']).format("DD/MM/YYYY HH:mm:ss");
    //     this.playerDate = moment(params['startDate']);
    //   }

    //   if (params['endDate'])
    //     this.endDate = moment(params['endDate']);

    //   if (params['mode'])
    //     this.playMode = params['mode'];

    //   this.onPlayerChanged.emit(this.getEventValue())
    // })

    this.trackers = [];
    this.units = [];

    effect(() => {
      const currentDate = this.planningStore.currentDate();
      this.startDate = currentDate ? moment(currentDate) : this.startDate;
    });

  }

  ngOnInit() {
    this.playerValueObs.pipe(takeUntil(this.destroy$)).subscribe(value => {
      this.playerValue = value;
      this.onPlayerChanged.emit(this.getEventValue());
    });

    if (this.user().language) {
      this.defaultLang = this.user().language;
      this.translate.setDefaultLang(this.defaultLang);
    } else {
      this.defaultLang = 'en';
      this.translate.setDefaultLang(this.defaultLang);
    }

    this.translate.get('connected').pipe(takeUntil(this.destroy$)).subscribe(res => { })
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getEventValue(): PlayerEventValue {
    return {
      speed: this.speed,
      playMode: this.playMode,
      playAllMode: this.playAllMode,
      playerState: this.playerState,
      startDate: this.startDate,
      endDate: this.endDate,
      playerDate: this.playerDate.toISOString(),
      playerValue: this.playerValue
    }
  }

  onPlay() {
    if (this.playerState == 'stop') {
      this.playerState = 'play';
      this.play();
      // $("#button_play").toggleClass('btn-success');
      // $("#button_play i").attr('class', "fa fa-pause");
    } else if (this.playerState == 'play' || this.playerState == 'resume') {
      this.playerState = 'pause';
      this.playAllMode = false;
      this.onPlayerChanged.emit(this.getEventValue());
      clearInterval(this.playerInterval);
      // $("#button_play i").attr('class', "fa fa-play");
    } else if (this.playerState == 'pause') {
      this.playerState = 'play';
      this.play();
      // $("#button_play i").attr('class', "fa fa-pause");
    }
  }

  stop() {
    clearInterval(this.playerInterval);
    this.playerState = 'stop';
    // $("#button_play").toggleClass('btn-success', false);
    // $("#button_play i").attr('class', "fa fa-play");
    this.playAllMode = false;
    this.playerValue = 0;
    this.onPlayerChanged.emit(this.getEventValue());
  }

  playAll() {
    this.playAllMode = this.playAllMode ? false : true;
  }

  record() {
    if (this.recordState == 'stop') {
      this.recordState = 'recording';

      this.startDate = moment();
      this.recordInterval = setInterval(() => {
        this.endDate = moment();
        this.currentDate = moment(this.endDate).format("DD/MM/YYYY HH:mm:ss");
        this.recordValue++;
      }, 1000);

      // $("#button_record").toggleClass('btn-success');
    } else if (this.recordState == 'recording') {
      this.recordState = 'stop';
      clearInterval(this.recordInterval);
      // $("#button_record").toggleClass('btn-success');
    }
  }

  stopRecord() {
    clearInterval(this.recordInterval);
    this.recordState = 'stop';
    // $("#button_record").toggleClass('btn-success', false);

    if (this.recordValue > 0)
      this.saveEvent();
    this.recordValue = 0;
  }

  /**
   * Set play mode realtime || replay
   *
   */
  setPlayMode(mode: string) {
    if (mode == "replay") {
      this.playMode = 'replay';

    } else {

      this.playMode = 'realtime';

      clearInterval(this.playerInterval);
      this.playerState = 'stop';

    }
    this.playerValue = 0;

  }

  /**
   * Set player duration
   *
   */
  setPlayerDuration() {
    var duration = this.endDate.diff(this.startDate, 'second');
    this.max = duration;
    this.playerDuration = duration;
    return this.playerDuration;
  };


  getPosition() {
    if (this.playMode === "realtime") {
      clearInterval(this.playerInterval);
      // self.socket.emit('lastPositionOfAllTrackers', '3d');
      // resetWorkerPosition();
    }

    if (this.playAllMode && this.playerValue <= this.setPlayerDuration()) {
      // this.getPosition();
      clearInterval(this.playerInterval);
    }
  }

  /**
   * Play Replay
   *
   */
  play() {
    if (this.playerValue <= this.setPlayerDuration()) {
      clearInterval(this.playerInterval);

      // if (this.viewerForge) this.viewerForge.resetTrace();
      if (!this.playAllMode) {

        this.playerInterval = setInterval(() => {
          this.playerValue += this.speed
          this.getPosition();

          this.onPlayerChanged.emit(this.getEventValue());
        }, 1000);
      } else {
        this.playerValue += this.speed;
      }
    } else {
      this.playerValue = 0;
      this.playAllMode = false;
      this.playerInterval = setInterval(() => {
        this.playerValue += this.speed;
        this.onPlayerChanged.emit(this.getEventValue());

      }, 1000);
    }
  }

  /**
   * Pause replay
   *
   */
  pauseViewer() {
    clearInterval(this.playerInterval);
    this.onPlayerChanged.emit(this.getEventValue())
  };


  /**
   * Play backward
   *
   */
  onReward() {
    this.playerState = 'reward';
    if (this.playerValue > 0)
      this.playerValue--;
    clearInterval(this.playerInterval);
    this.onPlayerChanged.emit(this.getEventValue())
  };

  onRewardHold() {

    // if (!this.holdTimeout) {
    //   this.holdTimeout = setTimeout(() => {
    //     clearTimeout(this.holdTimeout)
    //     this.holdTimeout = null;
    //   }, 200)

    this.playerState = 'reward-hold';
    this.startDate.subtract(1, 'day')
    clearInterval(this.playerInterval);
    this.onPlayerChanged.emit(this.getEventValue())
    // }
  };

  onForward() {
    this.playerState = 'forward';
    // if (this.playerValue < this.playerDuration)
    this.playerValue++;
    clearInterval(this.playerInterval);
    this.onPlayerChanged.emit(this.getEventValue())
  };

  onForwardHold() {
    // if (!this.holdTimeout) {
    // this.holdTimeout = setTimeout(() => {
    //   clearTimeout(this.holdTimeout)
    //   this.holdTimeout = null;
    // }, 200)

    this.playerState = 'forward-hold';
    this.startDate.add(1, 'day')
    clearInterval(this.playerInterval);
    this.onPlayerChanged.emit(this.getEventValue())
    // }
  };
  onFastReward() {
    this.playerState = 'reward';
    this.playerValue = 0;
    clearInterval(this.playerInterval);
    this.onPlayerChanged.emit(this.getEventValue())
  };

  onFastForward() {
    this.playerState = 'forward';
    this.playerValue = this.playerDuration;
    clearInterval(this.playerInterval);
    this.onPlayerChanged.emit(this.getEventValue())
  };
  /**
   * Play forward
   *
   */
  seekForwardViewer() {
    var playerValue = this.playerValue;
    var startDateInSeconds = moment(this.startDate).unix() * .001;
    var playedTime = startDateInSeconds + this.playerValue;
    var endDateInMilliseconds = moment(this.endDate).unix() * .001;

    if ((playedTime + this.speed > endDateInMilliseconds) || (playedTime === endDateInMilliseconds)) {
      this.playerValue = this.setPlayerDuration();
      clearInterval(this.playerInterval);
    } else {
      this.playerValue = playerValue + this.speed;
    }
    this.onPlayerChanged.emit(this.getEventValue())
  };

  /**
   * Create user generated event
   *
   */


  //  testeur(){
  //      document.getElementById('lat').style.background='yellow';
  //  }
  saveEvent() {
    // this.eventTitle = $('#eventTitle').val();
    // this.eventComment = $('#eventComment').val();
    const dialogRef = this.dialog.open(CreateEventDialogComponent, { data: this.getEventValue() })

    dialogRef.afterClosed().subscribe(res => {
      // if (res.event) {
      //   this.eventsService.addEvent(res.event).pipe(takeUntil(this.destroy$)).subscribe(res => {
      //     //
      //   })
      // }
    })
    // var event = {
    //   type: "User defined",
    //   title: this.eventTitle,
    //   comment: this.eventComment,
    //   startDate: moment.parseZone(this.startDate),
    //   endDate: moment.parseZone(this.endDate)
    // }

    // this.eventsService.updateEvent(event).pipe(takeUntil(this.destroy$)).subscribe(result => {

    //   // modal.hide();

    // })
  }
  /**
     * 
     * @param {number} iteration number of hour for to iterate (1, 6, 24)
     * @returns array of seconds amount
     * 
     */
  getSecondsArray(iteration: number = 1) {

    let secondsArray = [0];
    let ellapse = iteration * 3600;

    let diff = moment(this.endDate).diff(this.startDate, "hours");
    diff = Math.floor(diff / iteration);

    //if hour isn't on iteration we must find the next and prev one...
    function getIterations(hour: number) {
      let prevHour = hour;
      let nextHour = hour + iteration;
      if (hour % iteration !== 0) {
        prevHour = 0;
        nextHour = 0;
        while (nextHour < hour) {
          nextHour += iteration;
          if (nextHour < hour)
            prevHour = nextHour;
        }
      }
      return { prevHour, nextHour }
    }

    //get the diff between startDate and next iteration
    let startHour = this.startDate.hour();
    let endHour = this.endDate.hour();

    let startIterations = getIterations(startHour);
    let endIterations = getIterations(endHour);

    let firstIteration = moment(this.startDate).startOf("day").add(startIterations.prevHour, "hours");
    let lastIteration = moment(this.endDate).startOf("day").add(endIterations.prevHour, "hours");

    let startDiff = moment(this.startDate).diff(firstIteration, "seconds");
    let endDiff = moment(this.endDate).diff(lastIteration, "seconds");

    for (let i = 0; i <= diff; i++) {

      if (i == 0) {//get first interval

        let minuteFromZero = this.startDate.minutes();
        let secondFromZero = (startHour * 3600) + (minuteFromZero * 60) + this.startDate.seconds();

        secondsArray.push((startIterations.nextHour * 3600) - secondFromZero)

      } else if ((i == diff && startDiff > endDiff) || i < diff) {// add interval

        secondsArray.push(secondsArray[secondsArray.length - 1]! + ellapse);

      }
    }

    // set last data
    if (secondsArray[secondsArray.length - 1] !== this.setPlayerDuration()) secondsArray.push(this.setPlayerDuration())

    return secondsArray;

  }

  getLabelsAndPositions(secondsArray: number[], format: string): LabelPositions {
    let labelArray: string[] = [], ticksPositions: number[] = [];
    secondsArray.forEach((t: number, i: number) => {

      let newPos = (t / this.setPlayerDuration()) * 100;
      ticksPositions.push(newPos);

      //if interval is lower than a percent we skip the label to avoid text overlap
      if (i == 0 || (i > 0 && newPos - ticksPositions[i - 1]! > 4)) {
        let hour = moment(this.startDate).add(t, "seconds").format(format)
        labelArray.push(hour)
      } else {
        labelArray.push("")
      }
    })
    return { labelArray, ticksPositions }
  }


  onPickerChanged(result: any) {
    this.startDate = moment(result);
    // this.endDate = moment(result.endDate);
    this.onPlayerChanged.emit(this.getEventValue())

  }
  onPlayModeChanged(mode: string) {
    this.playMode = mode;
    this.onPlayerChanged.emit(this.getEventValue())
  }
  /**
   * Open create event modal
   *
   */
  showSaveEventModal() {
    // modal.show('/modules/viewer/modal-save-event.html', () => {
    //   $('#eventStartTime').daterangepicker({
    //     timePicker: true,
    //     timePickerIncrement: 1,
    //     startDate: this.startDate !== undefined ? this.startDate : moment().subtract(6, 'hours'),
    //     endDate: this.endDate !== undefined ? this.endDate : moment(),
    //     timePicker24Hour: true,
    //     timePickerSeconds: true,
    //     format: 'DD/MM/YYYY HH:mm:ss',
    //     locale: _LOCALES.datepicker
    //   },
    //     (start, end, label) => {

    //       this.startDate = new Date(start);
    //       this.endDate = new Date(end);
    //       this.setPlayerDuration();

    //     });
    // },
    //   () => this.saveEvent());
  }

}
